import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type { FilterChip } from './define'

export interface FilterChipsProps<TState> {
    chips: FilterChip<TState>[]
    onReset: (patch: Partial<TState>) => void
    onClearAll: () => void
    className?: string
}

/**
 * The applied filters, spelled out and individually removable.
 *
 * This is what makes a filtered console honest: a list showing 3 of 40,000 records
 * looks identical to a broken query unless the interface says why. Chips also give
 * the operator a way out of one filter without resetting the other six.
 */
export function FilterChips<TState>({ chips, onReset, onClearAll, className }: FilterChipsProps<TState>) {
    if (chips.length === 0) return null

    return (
        <div
            role="group"
            aria-label="Applied filters"
            // Chips sit shoulder to shoulder, so they are a cluster: they grow on a
            // coarse pointer and the gap grows with them rather than being hit-slopped.
            className={cn('flex flex-wrap items-center gap-1.5 pointer-coarse:gap-2', className)}>
            {chips.map(chip => (
                <button
                    key={chip.key}
                    type="button"
                    onClick={() => onReset(chip.reset)}
                    aria-label={`Remove filter ${chip.label}: ${chip.value}`}
                    className={cn(
                        'group inline-flex max-w-72 items-center gap-1 rounded-full border border-input bg-card',
                        'py-0.5 pl-2.5 pr-1 text-xs shadow-xs transition-colors duration-(--animate-duration-fast)',
                        'pointer-coarse:py-2',
                        'hover:border-destructive/40 hover:bg-destructive-soft',
                        'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring'
                    )}>
                    <span className="text-muted-foreground">{chip.label}</span>
                    <span className="truncate font-medium text-foreground">{chip.value}</span>
                    <X
                        aria-hidden
                        className="size-3 shrink-0 rounded-full text-muted-foreground group-hover:text-destructive-soft-foreground pointer-coarse:size-4"
                    />
                </button>
            ))}

            {/* One chip is faster to remove directly; past that, a reset is the intent. */}
            {chips.length > 1 && (
                <Button variant="ghost" size="sm" onClick={onClearAll} className="text-muted-foreground">
                    Clear all
                </Button>
            )}
        </div>
    )
}
